import { FC } from "react";
import { Link } from "react-router-dom";
import { clientContent } from "../../client/content";
import { withPage } from "../../components/Page";
import { Hero } from "../../components/Hero";
import { StatCard } from "../../components/StatCard";
import { TaskList } from "../../components/TaskList";
import { RecentItems } from "../../components/RecentItems";
import { NoticeBanner } from "../../components/NoticeBanner";
import { InfoCallout } from "../../components/InfoCallout";
import styles from "./Home.module.scss";

const Home: FC = () => {
  return (
    <main id="home" className={styles.homeContainer} tabIndex={-1}>
      <div className={styles.headerRow}>
        <div className={styles.headerTextGroup}>
          <span className={styles.eyebrow}>
            {clientContent.siteCode} · {clientContent.tagline.toUpperCase()}
          </span>
          <h1 className={styles.pageTitle}>
            Welcome to {clientContent.systemName}
          </h1>
          <p className={styles.pageSubtitle}>
            Find trusted building information, quickly and in context.
          </p>
        </div>
        <Link to="/search" className={styles.primaryButton}>
          Search all information
        </Link>
      </div>

      <NoticeBanner>
        <strong>Pulse Modernisation Demo:</strong> This is a demonstration of
        how the existing Pulse platform could be modernised and enhanced using
        advancements in technology such as AI, smarter search and QR code access
        — making building information faster to find, easier to use and more
        accessible in the office or on site, while retaining the familiar
        structure and information already in place.
      </NoticeBanner>

      <InfoCallout variant="warning" isHome>
        This is only a demo environment. Many links are illustrative and will do nothing.
      </InfoCallout>

      <div className={styles.demoSplitWrapper}>
        <div className={styles.demoBanner}>
          <div className={styles.demoBannerContent}>
            <strong className={styles.demoBannerTitle}>
              Key Areas to Test
            </strong>
            <p className={styles.demoBannerText}>
              Explore our modernisation concept across these core areas:
            </p>
            <ol
              className={styles.highlightsList}
              style={{ listStyleType: "decimal", paddingLeft: "1.8rem" }}
            >
              <li>
                <strong>Search engine</strong> (
                <a
                  href="/search"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /search
                </a>
                )
              </li>
              <li>
                <strong>Category page</strong> (
                <a
                  href="/design-criteria/mechanical"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /design-criteria/mechanical
                </a>
                )
              </li>
              <li>
                <strong>Drawings page</strong> (
                <a
                  href="/drawings"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /drawings
                </a>
                )
              </li>
              <li>
                <strong>QR code concept</strong> (
                <a
                  href="/asset-data/ahu-003"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /asset-data/ahu-003
                </a>
                )
              </li>
              <li>
                <strong>Ask AI concepts</strong> (Contextual buttons on pages &
                PDFs)
              </li>
              <li>
                <strong>Responsive design</strong> (Mobile, tablet & desktop)
              </li>
              <li>
                <strong>Modernised login</strong> (
                <a
                  href="/login"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /login
                </a>
                )
              </li>
              <li>
                <strong>Future capabilities</strong> (
                <a
                  href="/demo"
                  style={{ color: "#bae6fd", textDecoration: "underline" }}
                >
                  /demo
                </a>
                )
              </li>
            </ol>
          </div>
          <Link to="/demo" className={styles.demoBannerLink}>
            Explore Demo Guide →
          </Link>
        </div>

        <div className={styles.benefitsCard}>
          <h3>Key Benefits</h3>
          <ul className={styles.benefitsList}>
            <li>
              <strong>Find information faster</strong> — less time searching
              through documents & drawings.
            </li>
            <li>
              <strong>Access information anywhere</strong> — desktop, tablet and
              mobile, including on site.
            </li>
            <li>
              <strong>Reduce manual effort</strong> — make everyday information
              easier to find & manage.
            </li>
            <li>
              <strong>Improve confidence</strong> — clearer, more accessible
              building documentation.
            </li>
            <li>
              <strong>Preserve what works</strong> — modernise without
              disrupting familiar structures.
            </li>
            <li>
              <strong>Build for the future</strong> — add capabilities
              incrementally as needs evolve.
            </li>
          </ul>
        </div>
      </div>

      <Hero />

      <section className={styles.statsGrid} aria-label="Key building metrics">
        <StatCard
          label="Assets"
          value="4,862"
          subtext="124 linked this month"
          trendIcon="↑"
        />
        <StatCard
          label="Drawings"
          value="2,148"
          subtext="96% current revision"
        />
        <StatCard
          label="Documents"
          value="1,074"
          subtext="All disciplines available"
        />
        <StatCard
          label="Last update"
          value="12 Sep 2026"
          subtext="View update log"
          isLink
          linkHref="#update-log"
        />
      </section>

      <section
        className={styles.contentGrid}
        aria-label="Tasks and recent items"
      >
        <TaskList />
        <RecentItems />
      </section>

      <NoticeBanner>
        <strong>Design direction:</strong> the rebuild retains familiar Pulse
        categories and building information, while replacing the dense portal
        layout with focused tasks, reliable search and connected records.
      </NoticeBanner>
    </main>
  );
};

const { title, description } = clientContent.seo.home;

export default withPage(Home)({
  meta: {
    title,
    description,
  },
});
